import * as Yup from "yup";

// URL fields are pre-populated with "https://" as a hint. If the user never
// fills them in beyond that, treat the value as empty when persisting so we
// don't store a bare scheme.
export const cleanOptionalUrl = (val) => {
  if (typeof val !== "string") return val;
  const trimmed = val.trim();
  if (!trimmed) return "";
  if (/^https?:\/\/$/i.test(trimmed)) return "";
  return trimmed;
};

export const URL_INPUT_PLACEHOLDER = "https://";

export const getValidationSchemas = (translate, options = {}) => {
  const { requireAccountFields = false, requirePasswordConfirm = true } =
    options;

  let contactShape = {
    contactName: Yup.string().required(
      translate("businessForm.validation.contactName.required")
    ),
    email: Yup.string()
      .email(translate("businessForm.validation.email.invalid"))
      .required(translate("businessForm.validation.email.required")),
    phoneNumber: Yup.string()
      .matches(
        /^\d{10}$/,
        translate("businessForm.validation.phoneNumber.format")
      )
      .required(translate("businessForm.validation.phoneNumber.required")),
  };

  if (requireAccountFields) {
    contactShape.password = Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required");
    if (requirePasswordConfirm) {
      contactShape.confirmPassword = Yup.string()
        .oneOf([Yup.ref("password")], "Passwords do not match")
        .required("Please confirm your password");
    }
  }

  return [
    // Step 1: Contact
    Yup.object().shape(contactShape),

  // Step 2: Business Info
  Yup.object().shape({
    name: Yup.object().shape({
      en: Yup.string().required(
        translate("businessForm.validation.name.english.required")
      ),
      fr: Yup.string(),
    }),
    category: Yup.object().shape({
      en: Yup.string().required(
        translate("businessForm.validation.category.required")
      ),
      fr: Yup.string(),
    }),
    // The scheme-only placeholder "https://" (the hint we pre-populate the
    // field with) is not a valid URL, so skip the .url() check in that case
    // and treat it as empty.
    websiteUrl: Yup.string()
      .test(
        "optional-url",
        translate("businessForm.validation.websiteUrl.invalid"),
        (value) => {
          const cleaned = cleanOptionalUrl(value);
          if (!cleaned) return true;
          try {
            // eslint-disable-next-line no-new
            new URL(cleaned);
            return true;
          } catch {
            return false;
          }
        },
      )
      .nullable()
      .default(URL_INPUT_PLACEHOLDER),
    // Not pre-populated — Google profile is less universal.
    googleProfileUrl: Yup.string()
      .test(
        "optional-url",
        translate("businessForm.validation.googleProfileUrl.invalid"),
        (value) => {
          const cleaned = cleanOptionalUrl(value);
          if (!cleaned) return true;
          try {
            // eslint-disable-next-line no-new
            new URL(cleaned);
            return true;
          } catch {
            return false;
          }
        },
      )
      .nullable()
      .default(""),
  }),

  // Step 3: Coupon
  Yup.object().shape({
    offerTitle: Yup.object().shape({
      en: Yup.string().required(
        translate("businessForm.validation.offerTitle.english.required")
      ),
      fr: Yup.string(),
    }),
    offerDescription: Yup.object().shape({
      en: Yup.string().required(
        translate("businessForm.validation.offerDescription.english.required")
      ),
      fr: Yup.string(),
    }),
    termsAndExclusions: Yup.object().shape({
      en: Yup.string(),
      fr: Yup.string(),
    }),
    maxValue: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .typeError(translate("businessForm.validation.maxValue.invalid")),
  }),

    // Step 4: Preview
    Yup.object().shape({}),
  ];
};

// Merge all step schemas into one for single-page (edit-mode) validation.
export const getCombinedValidationSchema = (translate, options = {}) =>
  getValidationSchemas(translate, options).reduce(
    (acc, schema) => acc.concat(schema),
    Yup.object().shape({})
  );

export const getInitialValues = (existingData = null) => {
  const defaultValues = {
    contactName: "",
    name: { en: "", fr: "" },
    description: { en: "", fr: "" },
    websiteUrl: URL_INPUT_PLACEHOLDER,
    googleProfileUrl: "",
    category: { en: "", fr: "" },
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    offerTitle: { en: "", fr: "" },
    offerDescription: { en: "", fr: "" },
    termsAndExclusions: { en: "", fr: "" },
    maxValue: "",
    passcode: "",
    confirmPasscode: "",
  };

  if (!existingData) {
    return defaultValues;
  }

  return {
    contactName: existingData.contactName || defaultValues.contactName,
    name: existingData.name || defaultValues.name,
    description: existingData.description || defaultValues.description,
    websiteUrl: existingData.websiteUrl || defaultValues.websiteUrl,
    googleProfileUrl:
      existingData.googleProfileUrl || defaultValues.googleProfileUrl,
    category: existingData.category || defaultValues.category,
    email: existingData.email || defaultValues.email,
    phoneNumber: existingData.phoneNumber || defaultValues.phoneNumber,
    offerTitle: existingData.offerTitle || defaultValues.offerTitle,
    offerDescription:
      existingData.offerDescription || defaultValues.offerDescription,
    termsAndExclusions:
      existingData.termsAndExclusions || defaultValues.termsAndExclusions,
    maxValue: existingData.maxValue || defaultValues.maxValue,
    passcode: existingData.passcode || defaultValues.passcode,
    confirmPasscode:
      existingData.confirmPasscode ||
      existingData.passcode ||
      defaultValues.confirmPasscode,
  };
};

export const handleLogoChange = (setLogo, setLogoPreview) => (event) => {
  const file = event.target.files[0];
  if (file) {
    setLogo(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  }
};
